import React, {useCallback} from 'react';
import {useEditor, Element} from '@craftjs/core';
import {ExclamationCircleIcon, ExclamationTriangleIcon} from '@heroicons/react/24/outline';
import {EditorContentContainer} from 'components';
import {classNames} from 'utils';

interface IEditorToolboxProps {
  icon: React.JSX.Element;
  text: string;
  children: React.ReactElement;
  isContainer?: boolean;
  info?: string;
  disabled?: boolean;
  showExclamation?: boolean;
}

const EditorToolbox = ({icon, text, children, isContainer = false, info = '', disabled = false, showExclamation = false}: IEditorToolboxProps) => {
  const {connectors} = useEditor();

  const ref = useCallback(
    (node: HTMLDivElement) => {
      node &&
        !disabled &&
        connectors.create(
          node,
          isContainer ? (
            <Element
              canvas
              is={EditorContentContainer as unknown as Element}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                width: '100%',
                backgroundColor: 'white',
                padding: '30px 30px 30px 30px',
              }}></Element>
          ) : (
            children
          ),
        );
    },
    [disabled, connectors, children],
  );

  return (
    <div
      ref={ref}
      className={classNames(
        'relative flex flex-col gap-1 items-center border border-slate-300 dark:border-darkText p-2 rounded-md bg-slate-50 dark:bg-darkBg shadow-xs',
        disabled ? 'opacity-50 cursor-default' : 'hover:brightness-105 dark:hover:brightness-120 cursor-grab',
      )}>
      {React.cloneElement(icon, {className: 'size-10 text-slate-600 dark:text-white'})}
      <p className="text-slate-800 dark:text-white text-xs font-semibold">{text}</p>
      {!!info && <ExclamationCircleIcon className="size-5 text-yellow-500 absolute top-1.5 left-1.5 cursor-default" title={info} />}
      {showExclamation && (
        <ExclamationTriangleIcon className="size-5 text-red-500 absolute top-1.5 right-1.5 cursor-default" title="This component must be added" />
      )}
    </div>
  );
};

export default React.memo(EditorToolbox);
